import type { Dimension, Pack, UnitId } from "./units";

export const CATEGORIES = [
  "malt",
  "sugar",
  "hop",
  "yeast",
  "adjunct",
  "brewing_aid",
  "water",
  "chemical",
  "packaging",
  "keg",
  "supplies",
] as const;
export type Category = (typeof CATEGORIES)[number];

export const YEAST_FORMS = ["dry", "slurry", "liquid"] as const;
export type YeastForm = (typeof YEAST_FORMS)[number];

interface CategoryDefaults {
  label: string;
  dimension: Dimension;
  displayUnit: UnitId;
  packName: string | null;
}

/**
 * Starting values when an item is created. Every one of them can be changed per item; they exist so that
 * adding a malt does not mean answering five questions whose answer is always "pounds, in bags".
 */
export const CATEGORY_DEFAULTS: Record<Category, CategoryDefaults> = {
  malt: {
    label: "Malt & grain",
    dimension: "mass",
    displayUnit: "lb",
    packName: "bag",
  },
  sugar: {
    label: "Sugars & extracts",
    dimension: "mass",
    displayUnit: "lb",
    packName: "bag",
  },
  hop: {
    label: "Hops",
    dimension: "mass",
    displayUnit: "lb",
    packName: "box",
  },
  yeast: {
    label: "Yeast",
    dimension: "mass",
    displayUnit: "g",
    packName: "brick",
  },
  adjunct: {
    label: "Fruit, flavors & spices",
    dimension: "mass",
    displayUnit: "lb",
    packName: "pail",
  },
  brewing_aid: {
    label: "Brewing aids",
    dimension: "mass",
    displayUnit: "lb",
    packName: null,
  },
  /**
   * Brewing water salts and acids, apart from cleaning and sanitation (Scott, 2026-09-29): gypsum,
   * calcium chloride, lactic and phosphoric acid, mash stabilizer. Weighed by default; an acid sold by
   * the gallon is set to volume on its item.
   */
  water: {
    label: "Water treatment",
    dimension: "mass",
    displayUnit: "lb",
    packName: null,
  },
  chemical: {
    label: "Cleaning & chemicals",
    dimension: "mass",
    displayUnit: "lb",
    packName: "pail",
  },
  packaging: {
    label: "Packaging",
    dimension: "count",
    displayUnit: "each",
    packName: "case",
  },
  keg: {
    label: "Kegs",
    dimension: "count",
    displayUnit: "each",
    packName: null,
  },
  /**
   * What a brewery buys and keeps but does not brew with or sell beer in: hop bags, airlocks,
   * hydrometers and test kits, gaskets, boots (Tyler, 2026-09-24). Counted, one at a time.
   */
  supplies: {
    label: "Supplies & equipment",
    dimension: "count",
    displayUnit: "each",
    packName: null,
  },
};

interface YeastFormDefaults {
  label: string;
  dimension: Dimension;
  displayUnit: UnitId;
  pack: Pack | null;
}

/**
 * Yeast units follow the form (Tyler, 2026-09-18): dry bricks in grams, typically 500 g; harvested slurry in
 * a brink in pounds; new liquid pitches in bbl of pitching capacity. An item is a strain in one form.
 */
export const YEAST_FORM_DEFAULTS: Record<YeastForm, YeastFormDefaults> = {
  dry: {
    label: "Dry",
    dimension: "mass",
    displayUnit: "g",
    pack: { name: "brick", size: "500" },
  },
  slurry: { label: "Slurry (brink)", dimension: "mass", displayUnit: "lb", pack: null },
  liquid: {
    label: "Liquid pitch",
    dimension: "pitch",
    displayUnit: "bbl_pitch",
    pack: null,
  },
};

export function isCategory(value: string): value is Category {
  return (CATEGORIES as readonly string[]).includes(value);
}

/**
 * The ways each category's stock can be counted, its usual way first. Malt and hops are always weighed,
 * and a yeast is counted as its form fixes. A chemical, a brewing aid, a sugar, an adjunct or a water
 * treatment can be a powder or a liquid, so it is counted by weight or by volume as it is sold: Star San
 * and Nalco are liquids, PBW a powder (Tyler, 2026-10-01). The catalog does not say which, so the brewer
 * does, and an import says what the other system counted it as.
 */
export const COUNTED_WAYS: Record<Category, readonly Dimension[]> = {
  malt: ["mass"],
  sugar: ["mass", "volume"],
  hop: ["mass"],
  yeast: ["mass"],
  adjunct: ["mass", "volume", "count"],
  brewing_aid: ["mass", "volume", "count"],
  water: ["mass", "volume"],
  chemical: ["mass", "volume", "count"],
  packaging: ["count", "mass"],
  keg: ["count"],
  supplies: ["count", "mass", "volume"],
};

/** The ways an item of this category, and for a yeast this form, can be counted. */
export function countedWays(
  category: Category,
  yeastForm?: YeastForm | null,
): readonly Dimension[] {
  if (category === "yeast") return [defaultsFor(category, yeastForm).dimension];
  return COUNTED_WAYS[category];
}

export function defaultsFor(
  category: Category,
  yeastForm?: YeastForm | null,
): { dimension: Dimension; displayUnit: UnitId; pack: Pack | null } {
  const base = CATEGORY_DEFAULTS[category];
  if (category === "yeast" && yeastForm) {
    const form = YEAST_FORM_DEFAULTS[yeastForm];
    return {
      dimension: form.dimension,
      displayUnit: form.displayUnit,
      pack: form.pack,
    };
  }
  return {
    dimension: base.dimension,
    displayUnit: base.displayUnit,
    // A default pack name with no size is not a usable pack; the size comes from the supplier offering.
    pack: null,
  };
}
